import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { debounceTime, filter, map, switchMap } from 'rxjs';
import { environment } from '../environments/environment';
import { Logger, ThemeService, OrganizationService, ErrorDialogService, DialogData, GlobalErrorHandler } from './@shared';
import { AuthorizationService } from './@shared/services/authorization.service';
import { AuthAdapterService } from 'pr1-ui-components';

const log = new Logger('AppComponent');

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AppComponent implements OnInit {
  private profileInfo: any;
  private pageRedirected: boolean = false;
  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemeService,
    private titleService: Title,
    private organizationService: OrganizationService,
    private errorDialogService: ErrorDialogService,
    private injector: Injector,
    private authService: AuthorizationService,
    private authAdapterService: AuthAdapterService
  ) { }

  ngOnInit(): void {
    this.listenToAuthErrors();
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    // setup application insights
    if (environment.applicationInsights?.enabled) {
      var angularPlugin = new AngularPlugin();
      const appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.applicationInsights.connectionString,
          extensions: [angularPlugin],
          extensionConfig: {
            [angularPlugin.identifier]: { router: this.router, errorServices: [new GlobalErrorHandler(this.injector)] },
            enableCorsCorrelation: environment.applicationInsights.enableCorsCorrelation,
            enableRequestHeaderTracking: environment.applicationInsights.enableRequestHeaderTracking,
            enableResponseHeaderTracking: environment.applicationInsights.enableResponseHeaderTracking,
            correlationHeaderExcludedDomains: ['*.queue.core.windows.net']
          }
        }
      });
      appInsights.loadAppInsights();
    }

    if (environment.appMode == 'shopify') {
      this.authService.setPermissions(['org:shopify']);
      this.organizationService.setOrganizations(['shopify']);
    }
    else {
      const userValue$ = this.authAdapterService.user$;
      userValue$.subscribe(profile => {
        if (profile) {
          this.profileInfo = profile;
          if (this.pageRedirected) {
            this.redirectPage();
          }
          const userOrganizations = (profile["pr1.permissions"] ?? []).map(function (x: string) { return x.replace("org:", "") });
          if (userOrganizations.length > 0) {
            if (!this.organizationService.organizations) {
              this.organizationService.setOrganizations(userOrganizations);
            }
          } else {
            this.errorDialogService.openDialog({
              title: "Organizations not configured",
              message: "Organizations are not configured for you. Please contact administrator."
            } as DialogData)
          }
          if (localStorage.getItem('organization') && profile["role_permissions"]) {
            this.updateUserPermissions(localStorage.getItem('organization')?.toString(), profile["role_permissions"] ?? profile["pr1.permissions"]);
          } else {
            this.authService.setPermissions(profile["pr1.permissions"] ?? []);
          }
        }
      });
    }



    // Apply the theme the user has selected (or default theme)
    this.themeService.setDefaultTheme();

    // Change page title on navigation based on route data
    const onNavigationEnd = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

    onNavigationEnd
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        switchMap(route => route.data),
      )
      .subscribe(event => {
        this.pageRedirected = true;
        this.redirectPage();
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(title);
        }
      });

  }

  updateUserPermissions(orgname: any, permissionsList: any) {
    if (permissionsList) {
      const keysList = Object.keys(permissionsList);
      if (keysList && keysList.length > 0 && orgname) {
        const orgKey = keysList.find(x => x.toLowerCase().includes(orgname.toLowerCase()));
        if (orgKey) {
          const permissionsInfo = permissionsList[orgKey];
          if (permissionsInfo && permissionsInfo.length > 0) {
            const permisions = permissionsInfo.map((x: any) => x.permission_name);
            this.authService.setPermissions(permisions);
          }
        } else {
          this.authService.setPermissions([]);
        }
      }
    }
  }

  redirectPage() {
    if (this.profileInfo) {
      const currenturl = window.location.href;
      if (this.profileInfo['urltoredirect'] && !currenturl.includes(this.profileInfo['urltoredirect'])) {
        const redirectUrl = currenturl.replace('pr1core', this.profileInfo['urltoredirect']);
        try {
          if (new URL(redirectUrl).origin === window.location.origin) {
            window.location.href = redirectUrl;
          }
        } catch { /* invalid URL — do not redirect */ }
      }
    }
  }


  listenToAuthErrors() {
    this.authAdapterService.error$
      .pipe(
        debounceTime(1000)
      )
      .subscribe((err: any) => {
        if (err?.error?.includes('access_denied')) {
          this.authAdapterService.logout();
          alert(err);
        }
      });
  }
}

